import { getSessionHistory, sessionHasEvent, sessionHasAnyOf } from './sessionHistory';
import { fireCompoundEvent } from './microConversions';

/**
 * Check for compound events based on current action and session history.
 * Call this when significant user actions occur.
 */
export function checkCompoundEvents(currentAction: string): void {
  const history = getSessionHistory();

  // Engaged pricing viewer: Watched teaser 50%+ -> clicked pricing
  if (
    currentAction === 'pricing_click' &&
    sessionHasAnyOf(['video_teaser_50', 'video_teaser_75', 'video_teaser_complete'])
  ) {
    if (!sessionHasEvent('compound_engaged_pricing_viewer')) {
      fireCompoundEvent('engaged_pricing_viewer');
    }
  }

  // Video to conversion: Watched demo 50%+ -> submitted form
  if (
    currentAction === 'form_submit' &&
    sessionHasAnyOf(['video_demo_5min', 'video_demo_15min', 'video_demo_complete'])
  ) {
    if (!sessionHasEvent('compound_video_to_conversion')) {
      fireCompoundEvent('video_to_conversion');
    }
  }

  // Deep pricing engagement: Pricing form + scroll 100% + 2min
  // Check if we have all three signals for pricing page
  if (
    sessionHasEvent('pricing_gate_business') || sessionHasEvent('pricing_gate_free') ||
    sessionHasEvent('pricing_request_business') || sessionHasEvent('pricing_request_free')
  ) {
    const hasDeepScroll = sessionHasEvent('scroll_100_pricing');
    const hasTimeEngagement = sessionHasEvent('time_2min_pricing');

    if (hasDeepScroll && hasTimeEngagement && !sessionHasEvent('compound_deep_pricing_engagement')) {
      fireCompoundEvent('deep_pricing_engagement');
    }
  }

  // High intent session: 5+ pages + 5min total
  // This is detected by checking for pages_5 event and time_5min events
  if (sessionHasEvent('pages_5')) {
    const hasExtendedTime = sessionHasAnyOf([
      'time_5min_pricing',
      'time_5min_demo',
      'time_5min_features',
      'time_5min_enterprise',
      'time_2min_pricing', // 2min on pricing is also high intent
      'time_2min_demo',
    ]);

    if (hasExtendedTime && !sessionHasEvent('compound_high_intent_session')) {
      fireCompoundEvent('high_intent_session');
    }
  }
}

/**
 * Track pricing click for compound event detection.
 * Safe to call from any component - handles browser-only execution.
 */
export function onPricingClick(): void {
  if (typeof window === 'undefined') return;
  trackPricingClick();
}

/**
 * Hook into pricing CTA clicks to check for compound events
 */
export function trackPricingClick(): void {
  checkCompoundEvents('pricing_click');
}

/**
 * Hook into form submissions to check for compound events
 */
export function trackFormSubmit(): void {
  checkCompoundEvents('form_submit');
}

/**
 * Check compound events after scroll/time thresholds are hit
 */
export function checkEngagementCompounds(): void {
  checkCompoundEvents('engagement_update');
}
